import React from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveIcon from '@mui/icons-material/Remove';
import theme from './theme';

const CELL_STYLES = {
  cleared: { bg: '#ecfdf5', border: 'rgba(5, 150, 105, 0.25)', iconColor: '#059669' },
  warning: { bg: '#fff7ed', border: 'rgba(217, 119, 6, 0.3)', iconColor: '#d97706' },
  screened: { bg: theme.colors.bgSecondary, border: theme.colors.borderDefault, iconColor: theme.colors.textSecondary },
};

function PathogenCell({ cell }) {
  const style = CELL_STYLES[cell.status] || CELL_STYLES.screened;
  const Icon =
    cell.status === 'cleared' ? CheckCircleIcon : cell.status === 'warning' ? WarningAmberIcon : RemoveIcon;
  const rows = Array.isArray(cell.detailRows) ? cell.detailRows : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: `${theme.radius.md}px`,
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Icon sx={{ fontSize: 22, color: style.iconColor, flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.3 }}>
            {cell.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.5, lineHeight: 1.45 }}>
            {cell.detail}
          </Typography>
        </Box>
      </Box>

      {rows.length > 0 ? (
        <Box
          sx={{
            mt: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${theme.colors.borderDefault}`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
              gap: 1,
              px: 1,
              py: 0.75,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.colors.textSecondary }}>CFU/ml pre-UVC</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.colors.textSecondary }}>CFU/ml post-UVC</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.colors.textSecondary }}>Date</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.colors.textSecondary }}>Location</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.colors.textSecondary }}>Room</Typography>
          </Box>

          <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
            {rows.map((r, idx) => (
              <Box
                key={`${r.date || 'd'}-${r.location || 'l'}-${idx}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderTop: idx === 0 ? 'none' : `1px solid ${theme.colors.borderDefault}`,
                }}
              >
                <Typography sx={{ fontSize: 11, color: theme.colors.textPrimary }}>{r.cfuIdentified || '—'}</Typography>
                <Typography sx={{ fontSize: 11, color: theme.colors.textPrimary }}>{r.cfuPostUvc || '—'}</Typography>
                <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary }}>{r.date || '—'}</Typography>
                <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary }}>{r.highlightedLocation || '—'}</Typography>
                <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary }}>{r.location || '—'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
}

export default function PathogenShieldGrid({ pathogenCells, pathogenCount }) {
  const screenedLabel =
    pathogenCount > 0
      ? `${pathogenCount} organism${pathogenCount === 1 ? '' : 's'} screened per surface`
      : 'organisms screened per surface';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${theme.colors.borderDefault}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary }}>Pathogen shield</Typography>
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.5, lineHeight: 1.5 }}>
            Post-UVC clearance rate for every organism on the panel.
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.colors.textSecondary }}>{screenedLabel}</Typography>
      </Box>

      {pathogenCells.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', bgcolor: theme.colors.bgSecondary, borderRadius: 2 }}>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
            No organism categories recorded for this month.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {pathogenCells.map((cell) => (
            <Grid item xs={12} sm={6} md={4} key={cell.name}>
              <PathogenCell cell={cell} />
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}
